import * as React from "react";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha,
  Card,
  CardContent,
  Avatar,
  InputAdornment,
  CircularProgress,
  Alert,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  SearchOutlined,
  History as HistoryIcon,
  ReceiptLongRounded,
  RestoreRounded,
  RequestQuoteRounded,
  AccountBalanceWalletRounded,
  Inventory2Outlined,
  FileDownloadRounded,
  NotificationsNoneRounded,
  HelpOutlineRounded,
  VisibilityOutlined,
  TwoWheelerRounded,
  TrendingUpRounded,
  TrendingDownRounded,
  DescriptionOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AppNavbar from "../../../../components/AppNavbar";
import Header from "../../../../components/Header";
import SideMenu from "../../../../components/SideMenu";
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from "../../../dashboard/theme/customizations";
import AppTheme from "../../../../shared-theme/AppTheme";
import { formatNumberWithCommas } from "../../../../utils/generalFunctions";

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// ---- Paleta (inspirada en SuitOrbit Motopréstamo) ------------------------
const FP = {
  color: {
    surface: "#F4F6FB",
    cardBg: "#FFFFFF",
    cardBgSoft: "#F8FAFE",
    border: "#E2E8F0",
    borderStrong: "#CBD5E1",
    textPrimary: "#0F172A",
    textSecondary: "#475569",
    textMuted: "#94A3B8",
    navy: "#0B2545",
    navyDeep: "#081B33",
    navyMuted: "#1E3A6A",
    accentBlue: "#1B6CFB",
    accentBlueDeep: "#0F4FCC",
    info: "#0EA5E9",
    success: "#10B981",
    successDark: "#059669",
    warning: "#D97706",
    danger: "#DC2626",
    dangerSoft: "#FEE2E2",
    dangerText: "#B91C1C",
    statStrip: "#EEF2FA",
  },
  shadow: {
    soft: "0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.04)",
    navy: "0 10px 30px rgba(11,37,69,0.25)",
  },
};

// ---- Utils ----------------------------------------------------------------
const currency = (v) => `$${formatNumberWithCommas(parseFloat(v || 0).toFixed(2))}`;
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("es-DO");
  } catch {
    return String(d);
  }
};

const headersAuth = () => ({
  "Content-Type": "application/json",
  "X-Usuario-ID": localStorage.getItem("id"),
  "X-company-ID": localStorage.getItem("idcompany"),
});

const camposLegibles = (campos, categoriaFields) => {
  if (!campos || typeof campos !== "object") return [];
  const fieldsArr = Array.isArray(categoriaFields) ? categoriaFields : [];
  const out = [];
  for (const [k, v] of Object.entries(campos)) {
    if (v === null || v === undefined || v === "") continue;
    const def = fieldsArr.find((f) => String(f.key) === String(k) || String(f.name) === String(k));
    out.push({ key: k, label: def?.label || def?.name || k, value: String(v) });
  }
  return out;
};

const camposResumen = (campos, categoriaFields) => {
  const items = camposLegibles(campos, categoriaFields).slice(0, 3);
  return items.map((c) => `${c.label}: ${c.value}`).join(" · ");
};

// Busca un campo por su `label`/`name` (case insensitive) entre los campos
// legibles. Lo usamos para mostrar CHASIS / MARCA / MODELO / COLOR cuando la
// categoría los define como campos dinámicos.
const findCampo = (camposVis, ...needles) => {
  const lc = needles.map((n) => String(n).toLowerCase());
  return camposVis.find((c) => lc.some((n) => String(c.label).toLowerCase().includes(n)))?.value || "";
};

// ---- Building blocks -------------------------------------------------------
const StatusPill = ({ label, variant = "neutral", sx = {} }) => {
  const map = {
    neutral: { bg: "#F1F5F9", color: FP.color.textSecondary, border: FP.color.border },
    success: { bg: alpha(FP.color.success, 0.12), color: FP.color.successDark, border: alpha(FP.color.success, 0.4) },
    danger: { bg: FP.color.dangerSoft, color: FP.color.dangerText, border: alpha(FP.color.danger, 0.35) },
    info: { bg: alpha(FP.color.accentBlue, 0.12), color: FP.color.accentBlue, border: alpha(FP.color.accentBlue, 0.4) },
    warning: { bg: alpha(FP.color.warning, 0.12), color: FP.color.warning, border: alpha(FP.color.warning, 0.4) },
  };
  const s = map[variant] || map.neutral;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 24,
        px: 1.2,
        borderRadius: 999,
        fontSize: "0.7rem",
        fontWeight: 800,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        bgcolor: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        ...sx,
      }}
    >
      {label}
    </Box>
  );
};

const DetailField = ({ label, value }) => (
  <Box>
    <Typography
      sx={{
        color: FP.color.textSecondary,
        fontSize: "0.66rem",
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        mb: 0.4,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.95rem", fontWeight: 700, wordBreak: "break-word", lineHeight: 1.3 }}>
      {value || "—"}
    </Typography>
  </Box>
);

// Strip de stats al pie del card de Detalles (fondo azul claro).
const FootStat = ({ label, value, last }) => (
  <Box
    sx={{
      flex: 1,
      px: 2,
      py: 1.8,
      textAlign: "left",
      borderRight: last ? "none" : `1px solid ${FP.color.border}`,
    }}
  >
    <Typography
      sx={{
        color: FP.color.textSecondary,
        fontSize: "0.66rem",
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        mb: 0.4,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ color: FP.color.textPrimary, fontSize: "1.1rem", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </Typography>
  </Box>
);

// Métrica del Resumen Financiero con delta opcional (estilo SuitOrbit).
const FinMetric = ({ label, value, delta, deltaPositive }) => (
  <Box>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.4 }}>
      <Typography
        sx={{
          color: FP.color.textSecondary,
          fontSize: "0.66rem",
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      {delta != null && (
        <Stack direction="row" alignItems="center" spacing={0.4}>
          {deltaPositive ? (
            <TrendingUpRounded sx={{ fontSize: 14, color: FP.color.successDark }} />
          ) : (
            <TrendingDownRounded sx={{ fontSize: 14, color: FP.color.danger }} />
          )}
          <Typography
            sx={{
              color: deltaPositive ? FP.color.successDark : FP.color.danger,
              fontSize: "0.72rem",
              fontWeight: 800,
              letterSpacing: "0.02em",
            }}
          >
            {delta}
          </Typography>
        </Stack>
      )}
    </Stack>
    <Typography
      sx={{
        color: FP.color.textPrimary,
        fontSize: "1.35rem",
        fontWeight: 800,
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "-0.01em",
      }}
    >
      {value}
    </Typography>
  </Box>
);

// Mini stacked bar para mostrar distribución (Capital / Interés / Mora / Cargos).
const DistributionBar = ({ parts = [] }) => {
  const total = parts.reduce((s, p) => s + (Number(p.value) || 0), 0);
  const hasData = total > 0;
  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={0.5}>
        {parts.map((p, i) => (
          <Tooltip key={i} title={`${p.label}: ${currency(p.value)}`} placement="top" arrow>
            <Box
              sx={{
                flex: hasData ? (Number(p.value) || 0) / total || 0.001 : 1,
                height: 28,
                bgcolor: hasData ? p.color : alpha(p.color, 0.18),
                borderRadius: 0.6,
                transition: "flex .25s",
              }}
            />
          </Tooltip>
        ))}
      </Stack>
      <Stack direction="row" spacing={1.4} sx={{ mt: 1, flexWrap: "wrap" }}>
        {parts.map((p, i) => (
          <Stack key={i} direction="row" alignItems="center" spacing={0.6}>
            <Box sx={{ width: 8, height: 8, borderRadius: 0.4, bgcolor: p.color }} />
            <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color: FP.color.textSecondary, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              {p.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

// ---- Section card del historial (Facturación / Recuperaciones / Cargos) --
const SECTION_META = {
  facturacion: {
    title: "Facturación",
    subtitle: "Referencia Unitaria",
    icon: ReceiptLongRounded,
    color: FP.color.accentBlue,
  },
  recuperaciones: {
    title: "Recuperaciones",
    subtitle: "Movimiento Interno",
    icon: RestoreRounded,
    color: FP.color.warning,
  },
  cargos: {
    title: "Cargos cobrados",
    subtitle: "Cobros adicionales",
    icon: RequestQuoteRounded,
    color: FP.color.navy,
  },
};

const HistorialCard = ({ kind, items, renderHighlight }) => {
  const meta = SECTION_META[kind];
  const Icon = meta.icon;
  const latest = items[0];
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${FP.color.border}`,
        bgcolor: FP.color.cardBg,
        boxShadow: FP.shadow.soft,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ p: 2.2, display: "flex", flexDirection: "column", height: "100%" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.2 }}>
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1.2,
                bgcolor: alpha(meta.color, 0.12),
                color: meta.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "0.98rem", lineHeight: 1.2, textAlign: "left" }}>
                {meta.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: FP.color.textMuted,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  mt: 0.2,
                }}
              >
                {meta.subtitle}
              </Typography>
            </Box>
          </Stack>
          <Box
            sx={{
              minWidth: 26,
              height: 26,
              px: 0.8,
              borderRadius: 0.6,
              bgcolor: alpha(meta.color, 0.12),
              color: meta.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: "0.82rem",
            }}
          >
            {items.length}
          </Box>
        </Stack>

        <Divider sx={{ borderColor: FP.color.border, mx: -2.2 }} />

        <Box sx={{ pt: 1.4, flex: 1 }}>
          {!latest ? (
            <Typography sx={{ fontSize: "0.84rem", color: FP.color.textMuted, fontStyle: "italic" }}>
              Sin registros para este producto.
            </Typography>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: FP.color.textSecondary,
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  mb: 0.4,
                }}
              >
                {latest.fecha}
              </Typography>
              {renderHighlight(latest, meta.color)}
              {items.length > 1 && (
                <Typography
                  sx={{
                    mt: 1.2,
                    fontSize: "0.72rem",
                    color: FP.color.textMuted,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  + {items.length - 1} evento{items.length - 1 === 1 ? "" : "s"} más
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    </Card>
  );
};

// ---- Component principal --------------------------------------------------
export default function HistorialProductoReport(props) {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState([]);
  const [optionsLoading, setOptionsLoading] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [resultados, setResultados] = React.useState([]);
  const [searched, setSearched] = React.useState(false);
  const [error, setError] = React.useState("");

  // Debounced lookup
  React.useEffect(() => {
    const q = inputValue.trim();
    if (q.length < 2) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setOptionsLoading(true);
    const t = setTimeout(async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/api/productos/buscar?q=${encodeURIComponent(q)}&idcompany=${localStorage.getItem("idcompany")}`,
          { method: "GET", headers: headersAuth() }
        );
        const data = await response.json();
        if (cancelled) return;
        setOptions(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [inputValue]);

  const fetchHistorial = React.useCallback(async (productoId) => {
    if (!productoId) {
      setResultados([]);
      setSearched(false);
      return;
    }
    setError("");
    setLoading(true);
    setSearched(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/productos/reporte/historial?q=${encodeURIComponent(productoId)}&idcompany=${localStorage.getItem("idcompany")}`,
        { method: "GET", headers: headersAuth() }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Error al cargar el historial");
      setResultados(data.productos || []);
    } catch (e) {
      setError(e.message || "Error al cargar el historial del producto");
      setResultados([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelect = (event, value) => {
    setSelected(value);
    fetchHistorial(value?.id);
  };

  // El botón "Buscar": si hay un seleccionado lo reusa; si no, toma la primera
  // opción disponible o lanza el query con el texto crudo.
  const handleSearchClick = () => {
    if (selected?.id) {
      fetchHistorial(selected.id);
      return;
    }
    if (options.length > 0) {
      setSelected(options[0]);
      fetchHistorial(options[0].id);
      return;
    }
    const q = inputValue.trim();
    if (q.length >= 2) fetchHistorial(q);
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: "flex" }}>
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : FP.color.surface,
            overflow: "auto",
          })}
        >
          <Stack spacing={2} sx={{ alignItems: "center", mx: 3, pb: 5, mt: { xs: 8, md: 0 } }}>
            <Header parent={"Reportes"} child={"Historial de Producto"} />

            <Container maxWidth={false} sx={{ px: { xs: 2, sm: 2.5, md: 3 }, py: 3 }}>
              {/* ----- Top toolbar (titulo + tab + acciones) ----- */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 2.5 }}
              >
                <Stack direction="row" alignItems="center" spacing={2.2}>
                  <IconButton
                    onClick={() => navigate(-1)}
                    size="small"
                    sx={{
                      border: `1px solid ${FP.color.border}`,
                      borderRadius: 1.2,
                      bgcolor: "#FFFFFF",
                      width: 36,
                      height: 36,
                      color: FP.color.textSecondary,
                      "&:hover": { borderColor: FP.color.borderStrong, bgcolor: "#FFFFFF" },
                    }}
                  >
                    <ArrowBack sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Typography
                    sx={{
                      fontSize: { xs: "1.4rem", md: "1.6rem" },
                      fontWeight: 800,
                      color: FP.color.textPrimary,
                      letterSpacing: "-0.02em",
                      lineHeight: 1,
                    }}
                  >
                    Productos
                  </Typography>
                  <Box
                    sx={{
                      borderBottom: `2px solid ${FP.color.accentBlue}`,
                      pb: 0.6,
                      pt: 0.4,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.74rem",
                        fontWeight: 800,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: FP.color.textPrimary,
                      }}
                    >
                      Buscador de Historial
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton
                    sx={{
                      border: `1px solid ${FP.color.border}`,
                      borderRadius: 1.2,
                      width: 36,
                      height: 36,
                      color: FP.color.textSecondary,
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <NotificationsNoneRounded sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    sx={{
                      border: `1px solid ${FP.color.border}`,
                      borderRadius: 1.2,
                      width: 36,
                      height: 36,
                      color: FP.color.textSecondary,
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <HelpOutlineRounded sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Button
                    variant="contained"
                    startIcon={<FileDownloadRounded />}
                    disabled={resultados.length === 0}
                    sx={{
                      bgcolor: FP.color.navy,
                      color: "#FFFFFF",
                      borderRadius: 1.2,
                      px: 2.2,
                      height: 36,
                      boxShadow: "none",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.84rem",
                      "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                      "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
                    }}
                  >
                    Exportar
                  </Button>
                </Stack>
              </Stack>

              {/* ----- Card de búsqueda ----- */}
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${FP.color.border}`,
                  bgcolor: FP.color.cardBg,
                  boxShadow: FP.shadow.soft,
                  p: { xs: 2, sm: 2.5 },
                  mb: 3,
                }}
              >
                <Typography
                  sx={{
                    color: FP.color.textSecondary,
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    mb: 1.4,
                  }}
                >
                  Búsqueda rápida de producto
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.4}>
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={options}
                    value={selected}
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    onChange={handleSelect}
                    inputValue={inputValue}
                    onInputChange={(_, v) => setInputValue(v)}
                    loading={optionsLoading}
                    loadingText="Buscando…"
                    noOptionsText={inputValue.trim().length < 2 ? "Escribe al menos 2 caracteres" : "Sin resultados"}
                    filterOptions={(x) => x}
                    getOptionLabel={(o) => (o && o.nombre) ? o.nombre : ""}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    renderOption={(propsOpt, option) => {
                      const resumen = camposResumen(option.campos, option.categoria_fields);
                      return (
                        <Box
                          component="li"
                          {...propsOpt}
                          key={option.id}
                          sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start !important", py: 1 }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                            <Inventory2Outlined fontSize="small" sx={{ color: FP.color.textMuted }} />
                            <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }} noWrap>
                              {option.nombre || "(sin nombre)"}
                            </Typography>
                            {option.estado && <StatusPill label={option.estado} variant="neutral" />}
                          </Stack>
                          <Typography variant="caption" sx={{ pl: 3.5, lineHeight: 1.3, color: FP.color.textSecondary }}>
                            {option.categoria_nombre ? `${option.categoria_nombre} · ` : ""}
                            {resumen || `ID: ${option.id}`}
                          </Typography>
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Chasis, serial, placa, nombre o ID…"
                        variant="outlined"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !open) {
                            e.preventDefault();
                            handleSearchClick();
                          }
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start" sx={{ ml: 1, color: FP.color.textMuted }}>
                                <SearchOutlined sx={{ fontSize: 20 }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {optionsLoading ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 48,
                            borderRadius: 1.4,
                            bgcolor: "#F8FAFE",
                            fontSize: "0.94rem",
                            "& fieldset": { borderColor: FP.color.border },
                            "&:hover fieldset": { borderColor: FP.color.borderStrong },
                            "&.Mui-focused fieldset": { borderColor: FP.color.accentBlue },
                          },
                        }}
                      />
                    )}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSearchClick}
                    disabled={loading || (inputValue.trim().length < 2 && !selected)}
                    sx={{
                      bgcolor: FP.color.accentBlue,
                      color: "#FFFFFF",
                      borderRadius: 1.4,
                      height: 48,
                      minWidth: { xs: "100%", sm: 140 },
                      px: 3,
                      fontWeight: 800,
                      fontSize: "0.88rem",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      boxShadow: "none",
                      "&:hover": { bgcolor: FP.color.accentBlueDeep, boxShadow: "none" },
                      "&.Mui-disabled": { bgcolor: "#CBD5E1", color: "#FFFFFF" },
                    }}
                  >
                    {loading ? <CircularProgress size={20} sx={{ color: "#FFFFFF" }} /> : "Buscar"}
                  </Button>
                </Stack>
                {error && (
                  <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError("")}>
                    {error}
                  </Alert>
                )}
              </Card>

              {/* ----- Empty state ----- */}
              {!loading && searched && resultados.length === 0 && !error && (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${FP.color.border}`,
                    p: 4,
                    textAlign: "center",
                    bgcolor: FP.color.cardBg,
                  }}
                >
                  <Inventory2Outlined sx={{ fontSize: 48, color: FP.color.textMuted, mb: 1 }} />
                  <Typography variant="body1" fontWeight={800} sx={{ color: FP.color.textPrimary }}>
                    No se encontraron productos
                  </Typography>
                  <Typography variant="body2" sx={{ color: FP.color.textSecondary }}>
                    Verifica que el valor ingresado sea correcto.
                  </Typography>
                </Card>
              )}

              {/* ----- Resultados ----- */}
              {resultados.map((r) => {
                const camposVis = camposLegibles(r.producto.campos, r.producto.categoria_fields);
                const totalCobrado = parseFloat(r.totales.totalCobrado || 0);
                const totalCargos = parseFloat(r.totales.cargosPagados || 0);
                const capitalPagado = parseFloat(r.totales.capitalPagado || 0);
                const interesPagado = parseFloat(r.totales.interesPagado || 0);
                const moraPagado = parseFloat(r.totales.moraPagado || 0);
                const eventos = (r.pagos?.length || 0) + (r.recuperaciones?.length || 0) + (r.cargos_pagados?.length || 0);
                const isVendido = r.producto.estado === "vendido";
                const isIncautado = r.producto.estado === "incautado";
                const estadoLabel = isIncautado ? "Incautado" : isVendido ? "Vendido" : (r.producto.estado || "Disponible");

                // Mapping para el card de Detalles: priorizamos campos típicos
                // (chasis, marca, modelo, color) si la categoría los define.
                const chasis = findCampo(camposVis, "chasis", "serial", "id") || r.producto.id;
                const marca = findCampo(camposVis, "marca");
                const modelo = findCampo(camposVis, "modelo");
                const color = findCampo(camposVis, "color");
                // El resto de campos (no usados arriba) se muestran como "extras"
                const camposExtra = camposVis.filter((c) => {
                  const l = String(c.label).toLowerCase();
                  return !["chasis", "serial", "marca", "modelo", "color"].some((n) => l.includes(n));
                });

                const loanMap = {};
                (r.prestamos || []).forEach((p) => {
                  loanMap[p.id] = p;
                });

                const facturacionItems = (r.pagos || []).map((p) => {
                  const loan = loanMap[p.id_prestamo];
                  return {
                    id: p.id,
                    fecha: fmtDate(p.fecha_pago),
                    nombres: loan?.cliente_nombre || "—",
                    loan_id: loan?.loan_id || "",
                    monto: parseFloat(p.monto_pagado || 0),
                    capital: parseFloat(p.capitalPagado || 0),
                    interes: parseFloat(p.interesPagado || 0),
                    mora: parseFloat(p.moraPagado || 0),
                    estado: p.estado,
                  };
                });

                const recuperacionesItems = (r.recuperaciones || []).map((rec) => {
                  const loan = loanMap[rec.prestamo_id];
                  return {
                    id: rec.id,
                    fecha: fmtDate(rec.created_at),
                    nombres: loan?.cliente_nombre || "—",
                    loan_id: loan?.loan_id || "",
                    motivo: rec.motivo,
                    monto: parseFloat(rec.monto_recuperacion || 0),
                    prioridad: rec.prioridad,
                    estado: rec.status,
                  };
                });

                const cargosItems = (r.cargos_pagados || []).map((c) => {
                  const loan = loanMap[c.id_prestamo];
                  return {
                    id: c.id,
                    fecha: fmtDate(c.created_at),
                    categoria: c.categoria_nombre || "—",
                    monto: parseFloat(c.monto || 0),
                    loan_id: loan?.loan_id || "",
                  };
                });

                const distribucionParts = [
                  { label: "Capital", value: capitalPagado, color: FP.color.accentBlue },
                  { label: "Interés", value: interesPagado, color: FP.color.success },
                  { label: "Mora", value: moraPagado, color: FP.color.warning },
                  { label: "Cargos", value: totalCargos, color: FP.color.navy },
                ];

                return (
                  <Box key={r.producto.id}>
                    <Grid container spacing={3} alignItems="flex-start" sx={{ mb: 3 }}>
                      {/* ===== Detalles de la Unidad (izquierda) ===== */}
                      <Grid item size={{ xs: 12, lg: 8 }}>
                        <Card
                          elevation={0}
                          sx={{
                            borderRadius: 2,
                            border: `1px solid ${FP.color.border}`,
                            bgcolor: FP.color.cardBg,
                            boxShadow: FP.shadow.soft,
                            overflow: "hidden",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Box
                            sx={{
                              px: 2.6,
                              py: 1.8,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              borderBottom: `1px solid ${FP.color.border}`,
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1.2}>
                              <TwoWheelerRounded sx={{ color: FP.color.textPrimary, fontSize: 22 }} />
                              <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1.05rem" }}>
                                Detalles de la Unidad
                              </Typography>
                            </Stack>
                            <StatusPill
                              label={estadoLabel}
                              variant={isIncautado ? "danger" : isVendido ? "info" : "success"}
                            />
                          </Box>

                          <CardContent sx={{ p: { xs: 2, sm: 3 }, flex: 1 }}>
                            <Grid container spacing={3} alignItems="stretch">
                              <Grid item size={{ xs: 12, sm: 5, md: 5 }}>
                                <Box
                                  sx={{
                                    width: "100%",
                                    aspectRatio: "4 / 3",
                                    borderRadius: 1.6,
                                    background: `linear-gradient(135deg, ${FP.color.navy} 0%, ${FP.color.navyMuted} 100%)`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#FFFFFF",
                                    flexDirection: "column",
                                    gap: 0.6,
                                    position: "relative",
                                    overflow: "hidden",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      inset: 0,
                                      background:
                                        "radial-gradient(400px 200px at 100% 0%, rgba(27,108,251,0.35), transparent 60%)",
                                    }}
                                  />
                                  <VisibilityOutlined sx={{ fontSize: 44, opacity: 0.85, position: "relative" }} />
                                  <Typography
                                    sx={{
                                      fontSize: "0.66rem",
                                      letterSpacing: "0.16em",
                                      textTransform: "uppercase",
                                      color: "rgba(255,255,255,0.8)",
                                      fontWeight: 800,
                                      position: "relative",
                                    }}
                                  >
                                    Vista del Producto
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item size={{ xs: 12, sm: 7, md: 7 }}>
                                <Grid container spacing={2.2}>
                                  <Grid item size={6}>
                                    <DetailField label="Chasis / ID" value={chasis} />
                                  </Grid>
                                  <Grid item size={6}>
                                    <DetailField label="Sucursal" value={r.producto.almacen_nombre || r.producto.sucursal_nombre} />
                                  </Grid>
                                  <Grid item size={6}>
                                    <DetailField label="Marca" value={marca} />
                                  </Grid>
                                  <Grid item size={6}>
                                    <DetailField label="Modelo" value={modelo} />
                                  </Grid>
                                  <Grid item size={6}>
                                    <DetailField label="Color" value={color} />
                                  </Grid>
                                  <Grid item size={6}>
                                    <DetailField label="Categoría" value={r.producto.categoria_nombre} />
                                  </Grid>
                                  {camposExtra.map((c) => (
                                    <Grid item size={6} key={c.key}>
                                      <DetailField label={c.label} value={c.value} />
                                    </Grid>
                                  ))}
                                  <Grid item size={6}>
                                    <DetailField label="Precio" value={currency(r.producto.precio)} />
                                  </Grid>
                                  <Grid item size={6}>
                                    <DetailField label="Costo" value={currency(r.producto.costo)} />
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </CardContent>

                          {/* Strip de stats al pie */}
                          <Box
                            sx={{
                              display: "flex",
                              bgcolor: FP.color.statStrip,
                              borderTop: `1px solid ${FP.color.border}`,
                            }}
                          >
                            <FootStat label="Total Gastos" value={currency(totalCargos)} />
                            <FootStat label="Total Recibido" value={currency(totalCobrado)} />
                            <FootStat label="Eventos Registrados" value={String(eventos)} last />
                          </Box>
                        </Card>
                      </Grid>

                      {/* ===== Columna derecha: Resumen + Estado ===== */}
                      <Grid item size={{ xs: 12, lg: 4 }}>
                        <Stack spacing={3} sx={{ width: "100%", alignItems: "stretch", textAlign: "left" }}>
                          {/* Resumen Financiero */}
                          <Card
                            elevation={0}
                            sx={{
                              borderRadius: 2,
                              border: `1px solid ${FP.color.border}`,
                              bgcolor: FP.color.cardBg,
                              boxShadow: FP.shadow.soft,
                              overflow: "hidden",
                              width: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                px: 2.4,
                                py: 1.6,
                                borderBottom: `1px solid ${FP.color.border}`,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  color: FP.color.textPrimary,
                                  fontSize: "0.78rem",
                                  letterSpacing: "0.14em",
                                  textTransform: "uppercase",
                                  textAlign: "left",
                                }}
                              >
                                Resumen Financiero
                              </Typography>
                            </Box>
                            <CardContent sx={{ p: 2.4, textAlign: "left" }}>
                              <Stack spacing={2} sx={{ alignItems: "stretch" }}>
                                <FinMetric
                                  label="Total Recibido"
                                  value={currency(totalCobrado)}
                                  delta="+0% vs prev."
                                  deltaPositive
                                />
                                <Divider sx={{ borderColor: FP.color.border }} />
                                <FinMetric
                                  label="Total Gastos"
                                  value={currency(totalCargos)}
                                  delta="+0% vs prev."
                                  deltaPositive={false}
                                />
                                <Divider sx={{ borderColor: FP.color.border }} />
                                <FinMetric label="Eventos en historial" value={String(eventos)} />
                                <DistributionBar parts={distribucionParts} />
                              </Stack>
                            </CardContent>
                          </Card>

                        </Stack>
                      </Grid>
                    </Grid>

                    {/* ===== Historial de propietarios y deudores ===== */}
                    <Box sx={{ mb: 3 }}>
                      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1.6 }}>
                        <HistoryIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
                        <Typography
                          sx={{
                            fontWeight: 800,
                            color: FP.color.textPrimary,
                            fontSize: "0.78rem",
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                          }}
                        >
                          Historial de Propietarios y Deudores
                        </Typography>
                      </Stack>
                      <Grid container spacing={3}>
                        <Grid item size={{ xs: 12, md: 6, lg: 4 }}>
                          <HistorialCard
                            kind="facturacion"
                            items={facturacionItems}
                            renderHighlight={(item, accent) => (
                              <>
                                <Typography sx={{ color: accent, fontWeight: 800, fontSize: "1rem", mb: 0.6, textAlign: "left" }}>
                                  {item.nombres}
                                  {item.loan_id ? ` · ${item.loan_id}` : ""}
                                </Typography>
                                <Typography sx={{ fontSize: "0.84rem", color: FP.color.textSecondary, lineHeight: 1.55 }}>
                                  Pagó {currency(item.monto)} — capital {currency(item.capital)}, interés {currency(item.interes)}, mora {currency(item.mora)}
                                </Typography>
                              </>
                            )}
                          />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6, lg: 4 }}>
                          <HistorialCard
                            kind="recuperaciones"
                            items={recuperacionesItems}
                            renderHighlight={(item, accent) => (
                              <>
                                <Typography sx={{ color: accent, fontWeight: 800, fontSize: "1rem", mb: 0.6 }}>
                                  {item.nombres}
                                  {item.loan_id ? ` · ${item.loan_id}` : ""}
                                </Typography>
                                <Typography sx={{ fontSize: "0.84rem", color: FP.color.textSecondary, lineHeight: 1.55 }}>
                                  {item.motivo || "Recuperación"} por {currency(item.monto)} — prioridad {item.prioridad || "—"}, estado {item.estado || "—"}
                                </Typography>
                              </>
                            )}
                          />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6, lg: 4 }}>
                          <HistorialCard
                            kind="cargos"
                            items={cargosItems}
                            renderHighlight={(item, accent) => (
                              <>
                                <Typography sx={{ color: accent, fontWeight: 800, fontSize: "1rem", mb: 0.6 }}>
                                  {item.categoria}
                                </Typography>
                                <Typography sx={{ fontSize: "0.84rem", color: FP.color.textSecondary, lineHeight: 1.55 }}>
                                  Cobrado {currency(item.monto)}
                                  {item.loan_id ? ` — préstamo ${item.loan_id}` : ""}
                                </Typography>
                              </>
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                );
              })}
            </Container>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
